import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { API_BASE_URL } from '../../config';
import { IAsset, ICategory, IBadge } from '../../types/damTypes';
import { isStoreAsset } from '../utils/storeAsset';

export default function MobileAssetGrid(props: {
  assets: IAsset[];
  allCategories: ICategory[];
  columns: 1 | 2;
  loading: boolean;
  error?: string | null;
  onSelectAsset: (assetId: string) => void;
  onAddToCart: (assetId: string) => void;
}) {
  const { assets, allCategories, columns, loading, error, onSelectAsset, onAddToCart } = props;

  const gridTemplateColumns = columns === 2 ? 'repeat(2, minmax(0, 1fr))' : 'repeat(1, minmax(0, 1fr))';

  const categoryNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    (allCategories || []).forEach((c) => {
      if (c && c._id) m.set(c._id, c.name);
    });
    return m;
  }, [allCategories]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!assets || assets.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Inga assets hittades
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Prova att söka eller välj en annan kategori.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns, gap: 1.25, p: 1.25 }}>
      {assets.map((asset) => {
        const previewPath = asset.previewPaths?.thumbnail || asset.previewPaths?.medium;
        const isStore = isStoreAsset(asset, allCategories);

        const categoryNames = (asset.categories || [])
          .map((id) => categoryNameById.get(id))
          .filter((n): n is string => !!n);
        const visibleCategoryNames = categoryNames.slice(0, 3);

        const badges = (asset.activeBadges || [])
          .filter((b): b is IBadge => !!b && typeof b === 'object' && !!(b as any)._id)
          .slice(0, 2);

        return (
          <Paper
            key={asset._id}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'white',
              cursor: 'pointer',
            }}
            onClick={() => onSelectAsset(asset._id)}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                bgcolor: '#e9ecef',
                aspectRatio: '1 / 1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {previewPath ? (
                <Box
                  component="img"
                  alt={asset.title || asset.fileName}
                  src={`${API_BASE_URL}${previewPath}`}
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    p: 1,
                  }}
                />
              ) : (
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 42, color: 'text.secondary' }} />
              )}

              {/* Store: add-to-cart (no stock prefetch; backend validates) */}
              {isStore && (
                <Tooltip title="Lägg i varukorg">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddToCart(asset._id);
                    }}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      bottom: 8,
                      bgcolor: '#072f53',
                      color: 'white',
                      '&:hover': { bgcolor: '#051a2e' },
                    }}
                    aria-label="Lägg i varukorg"
                  >
                    <AddShoppingCartIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Badges (display only) */}
              {badges.length > 0 && (
                <Box sx={{ position: 'absolute', right: 8, bottom: 8, display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {badges.map((b) => {
                    const name = b?.name || 'Badge';
                    const showName = b?.showName !== false;
                    const text = showName ? name : '';
                    const emoji = b?.displayEmoji || '';
                    const tooltipLabel = `${emoji ? `${emoji} ` : ''}${name}`.trim();
                    return (
                    <Box
                      key={b._id}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 22,
                        minWidth: 22,
                        px: 0.75,
                        borderRadius: 999,
                        bgcolor: b.color || '#072f53',
                        color: b.textColor || '#fff',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                        fontSize: '0.75rem',
                        lineHeight: 1,
                        gap: 0.5,
                        maxWidth: columns === 2 ? 120 : 220,
                      }}
                      title={tooltipLabel}
                    >
                      {b.iconUrl ? (
                        <Box component="img" src={b.iconUrl} alt="icon" sx={{ width: 14, height: 14, borderRadius: 0.5 }} />
                      ) : (
                        <span>{emoji || '•'}</span>
                      )}
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {text}
                      </span>
                    </Box>
                  );})}
                </Box>
              )}
            </Box>

            <Box sx={{ p: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }} noWrap>
                {(asset.title && asset.title.trim()) ? asset.title : asset.fileName}
              </Typography>

              {/* Categories (desktop-style grey chips) */}
              {visibleCategoryNames.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                  {visibleCategoryNames.map((name) => (
                    <Chip
                      key={name}
                      label={name}
                      size="small"
                      variant="outlined"
                      sx={{
                        bgcolor: '#f0f0f0',
                        borderColor: '#e0e0e0',
                        height: 20,
                        fontSize: '0.72rem',
                      }}
                    />
                  ))}
                  {categoryNames.length > visibleCategoryNames.length && (
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      +{categoryNames.length - visibleCategoryNames.length}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}

